/**
 * Visual tokens aligned with HomePage `RegulationsTable` (MUI DataGrid list).
 * Keep triage / task tables visually consistent with the regulations list.
 */
export const HOME_LIST_TABLE_TOKENS = {
  fontFamily: '"Nunito Sans", sans-serif',
  headerBackground: "#f9fafb",
  headerColor: "#262626",
  bodyBackground: "#ffffff",
  cellColor: "#262626",
  inactiveColor: "#8c8c8c",
  rowDivider: "#f2f2f2",
  fontSize: 14,
  headerFontWeight: 600,
  cellFontWeight: 500,
  headerHeight: 48,
  /** Keep rows stable when cells swap between "—" and form controls. */
  cellControlMinHeight: 40,
  rowMinHeight: 64,
  cellPaddingX: 16,
  inputBorderColor: "#d9d9d9",
  focusBorderColor: "#295ef1",
  /**
   * Triage table column widths (table-layout: fixed).
   * Index / assessment_required share this % each; remaining columns split
   * the rest equally so toggling assessment_required does not reflow layout.
   */
  triageNarrowColumnWidthPercent: 17,
  /**
   * Overall Review / wide SME approve: index (SME Category).
   * minWidth keeps long category labels readable when the table scrolls on narrow viewports.
   */
  overallApproveIndexColumnWidthPx: 220,
  /** Short Yes/No select columns (Impact On Process / Product, etc.). */
  overallApproveSelectColumnWidthPx: 132,
  /** Overall Review: Approve or Reject sticky column. */
  approveColumnWidthPx: 168,
  /** Overall Review: fixed Reject Comments column so Approve/Reject toggle does not reflow. */
  approvalCommentsColumnWidthPx: 280,
  /**
   * Long comment columns (Assessment / Review Comments) base width.
   * Wider than left selects so leftover space on large screens prefers comments.
   */
  longTextColumnWidthPx: 280,
  /** Overall Review: SME Assessment attachments (filename + download / View More). */
  attachmentColumnWidthPx: 220,
} as const;

/** Comment preview columns that grow with table width (not sticky). */
export const OVERALL_APPROVE_GROWING_LONG_TEXT_FIELD_NAMES = [
  "assessmentComments",
  "reviewComments",
] as const;

export const OVERALL_APPROVE_ATTACHMENT_FIELD_NAMES = [
  "taskAttachment",
  "attachment",
] as const;

export const TRIAGE_INDEX_COLUMN_WIDTH_SX = {
  width: `${HOME_LIST_TABLE_TOKENS.triageNarrowColumnWidthPercent}%`,
  maxWidth: `${HOME_LIST_TABLE_TOKENS.triageNarrowColumnWidthPercent}%`,
} as const;

export const TRIAGE_ASSESSMENT_REQUIRED_COLUMN_WIDTH_SX = {
  width: `${HOME_LIST_TABLE_TOKENS.triageNarrowColumnWidthPercent}%`,
  maxWidth: `${HOME_LIST_TABLE_TOKENS.triageNarrowColumnWidthPercent}%`,
} as const;

export const APPROVE_COLUMN_WIDTH_SX = {
  width: HOME_LIST_TABLE_TOKENS.approveColumnWidthPx,
  minWidth: HOME_LIST_TABLE_TOKENS.approveColumnWidthPx,
  maxWidth: HOME_LIST_TABLE_TOKENS.approveColumnWidthPx,
} as const;

export const APPROVAL_COMMENTS_COLUMN_WIDTH_SX = {
  width: HOME_LIST_TABLE_TOKENS.approvalCommentsColumnWidthPx,
  minWidth: HOME_LIST_TABLE_TOKENS.approvalCommentsColumnWidthPx,
  maxWidth: HOME_LIST_TABLE_TOKENS.approvalCommentsColumnWidthPx,
} as const;

export const LONG_TEXT_COLUMN_WIDTH_SX = {
  width: HOME_LIST_TABLE_TOKENS.longTextColumnWidthPx,
  minWidth: HOME_LIST_TABLE_TOKENS.longTextColumnWidthPx,
} as const;

export const ATTACHMENT_COLUMN_WIDTH_SX = {
  width: HOME_LIST_TABLE_TOKENS.attachmentColumnWidthPx,
  minWidth: HOME_LIST_TABLE_TOKENS.attachmentColumnWidthPx,
} as const;

export const OVERALL_APPROVE_INDEX_COLUMN_WIDTH_SX = {
  width: HOME_LIST_TABLE_TOKENS.overallApproveIndexColumnWidthPx,
  minWidth: HOME_LIST_TABLE_TOKENS.overallApproveIndexColumnWidthPx,
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
} as const;

export const OVERALL_APPROVE_SELECT_COLUMN_WIDTH_SX = {
  width: HOME_LIST_TABLE_TOKENS.overallApproveSelectColumnWidthPx,
  minWidth: HOME_LIST_TABLE_TOKENS.overallApproveSelectColumnWidthPx,
} as const;

/** Approve / Reject Comments only — no wide assessment/review comment columns. */
export const COMPACT_APPROVE_COLUMN_FIELD_NAMES = [
  "approve",
  "approvalComments",
] as const;

/**
 * Base (min) width for a wide Overall Review / SME approve column.
 * Sticky Approve / Reject Comments stay fixed; other columns can grow with leftover space.
 */
export function getOverallApproveColumnWidthPx(fieldName: string): number {
  if (fieldName === "index") {
    return HOME_LIST_TABLE_TOKENS.overallApproveIndexColumnWidthPx;
  }
  if (fieldName === "approve") {
    return HOME_LIST_TABLE_TOKENS.approveColumnWidthPx;
  }
  if (fieldName === "approvalComments") {
    return HOME_LIST_TABLE_TOKENS.approvalCommentsColumnWidthPx;
  }
  if (
    (OVERALL_APPROVE_GROWING_LONG_TEXT_FIELD_NAMES as readonly string[]).includes(
      fieldName,
    )
  ) {
    return HOME_LIST_TABLE_TOKENS.longTextColumnWidthPx;
  }
  if (
    (OVERALL_APPROVE_ATTACHMENT_FIELD_NAMES as readonly string[]).includes(
      fieldName,
    )
  ) {
    return HOME_LIST_TABLE_TOKENS.attachmentColumnWidthPx;
  }
  return HOME_LIST_TABLE_TOKENS.overallApproveSelectColumnWidthPx;
}

export function getOverallApproveColumnWidthSx(fieldName: string) {
  if (fieldName === "index") {
    return OVERALL_APPROVE_INDEX_COLUMN_WIDTH_SX;
  }
  if (fieldName === "approve") {
    return APPROVE_COLUMN_WIDTH_SX;
  }
  if (fieldName === "approvalComments") {
    return APPROVAL_COMMENTS_COLUMN_WIDTH_SX;
  }
  if (
    (OVERALL_APPROVE_GROWING_LONG_TEXT_FIELD_NAMES as readonly string[]).includes(
      fieldName,
    )
  ) {
    return LONG_TEXT_COLUMN_WIDTH_SX;
  }
  if (
    (OVERALL_APPROVE_ATTACHMENT_FIELD_NAMES as readonly string[]).includes(
      fieldName,
    )
  ) {
    return ATTACHMENT_COLUMN_WIDTH_SX;
  }
  return OVERALL_APPROVE_SELECT_COLUMN_WIDTH_SX;
}

/** Sum of column base widths — drives horizontal scroll on narrow viewports. */
export function getWideOverallApproveTableMinWidthPx(
  columnFieldNames: readonly string[],
): number {
  return (
    getOverallApproveColumnWidthPx("index") +
    columnFieldNames.reduce(
      (sum, name) => sum + getOverallApproveColumnWidthPx(name),
      0,
    )
  );
}

/**
 * SME Assessment by Category (and similar approve tables) only have index +
 * Approve / Reject Comments — split columns evenly instead of sticky fixed px.
 */
export function isCompactEqualWidthApproveTable(
  isOverallApproveTable: boolean,
  columnFieldNames: readonly string[],
): boolean {
  if (!isOverallApproveTable || columnFieldNames.length === 0) return false;
  const compact = new Set<string>(COMPACT_APPROVE_COLUMN_FIELD_NAMES);
  return columnFieldNames.every((fieldName) => compact.has(fieldName));
}

/** Equal % widths for compact approve tables (table-layout: fixed). */
export function getEqualColumnWidthSx(totalColumnCount: number) {
  if (totalColumnCount <= 0) return undefined;
  const width = `${100 / totalColumnCount}%`;
  return {
    width,
    maxWidth: width,
  } as const;
}

/** Trailing sticky columns for Overall Review (right → left visual order). */
export const OVERALL_APPROVE_STICKY_FIELD_NAMES = [
  "approvalComments",
  "approve",
] as const;

export function getOverallApproveStickyColumnWidthPx(
  fieldName: string,
): number | undefined {
  if (fieldName === "approvalComments") {
    return HOME_LIST_TABLE_TOKENS.approvalCommentsColumnWidthPx;
  }
  if (fieldName === "approve") {
    return HOME_LIST_TABLE_TOKENS.approveColumnWidthPx;
  }
  return undefined;
}

/**
 * Sticky right offsets for Approve + Reject Comments.
 * Schema does not need a `fixed` flag — pin by field name from the right edge.
 */
export function buildOverallApproveStickyRightByField(
  fieldNamesFromLeftToRight: readonly string[],
): Map<string, number> {
  const stickySet = new Set<string>(OVERALL_APPROVE_STICKY_FIELD_NAMES);
  const rightByField = new Map<string, number>();
  let accumulatedRight = 0;

  for (let i = fieldNamesFromLeftToRight.length - 1; i >= 0; i -= 1) {
    const fieldName = fieldNamesFromLeftToRight[i];
    if (!stickySet.has(fieldName)) {
      // Only pin a contiguous trailing sticky group.
      break;
    }
    rightByField.set(fieldName, accumulatedRight);
    accumulatedRight += getOverallApproveStickyColumnWidthPx(fieldName) ?? 0;
  }

  return rightByField;
}

/** Sticky right edge for Approve / Reject Comments on Overall Review tables. */
export function getOverallApproveStickyColumnSx(
  fieldName: string,
  options?: {
    isHeader?: boolean;
    rightPx?: number;
    isLeftmostSticky?: boolean;
  },
): Record<string, unknown> | undefined {
  const rightPx = options?.rightPx;
  if (rightPx == null || !Number.isFinite(rightPx)) {
    return undefined;
  }

  const isHeader = options?.isHeader === true;
  // Keep sticky cells above scrolling body cells (same white bg otherwise hides them).
  const zIndex = (isHeader ? 5 : 3) + (fieldName === "approvalComments" ? 1 : 0);
  const bgcolor = isHeader
    ? HOME_LIST_TABLE_TOKENS.headerBackground
    : HOME_LIST_TABLE_TOKENS.bodyBackground;

  return {
    position: "sticky",
    right: rightPx,
    zIndex,
    bgcolor,
    ...(options?.isLeftmostSticky
      ? { boxShadow: "-6px 0 8px -6px rgba(0, 0, 0, 0.12)" }
      : null),
  };
}

export const HOME_LIST_TABLE_CONTAINER_SX = {
  width: "100%",
  bgcolor: HOME_LIST_TABLE_TOKENS.bodyBackground,
  border: "none",
  boxShadow: "none",
  borderRadius: 0,
  overflow: "hidden",
} as const;

export const HOME_LIST_TABLE_SX = {
  borderCollapse: "separate",
  borderSpacing: 0,
  width: "100%",
  bgcolor: HOME_LIST_TABLE_TOKENS.bodyBackground,
  "& .MuiTableCell-root": {
    borderRight: "none",
    borderLeft: "none",
    fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
    fontSize: HOME_LIST_TABLE_TOKENS.fontSize,
    color: HOME_LIST_TABLE_TOKENS.cellColor,
    verticalAlign: "middle",
  },
} as const;

export const HOME_LIST_TABLE_HEAD_ROW_SX = {
  bgcolor: HOME_LIST_TABLE_TOKENS.headerBackground,
  "& .MuiTableCell-root": {
    bgcolor: HOME_LIST_TABLE_TOKENS.headerBackground,
    color: HOME_LIST_TABLE_TOKENS.headerColor,
    fontWeight: HOME_LIST_TABLE_TOKENS.headerFontWeight,
    fontSize: HOME_LIST_TABLE_TOKENS.fontSize,
    lineHeight: "20px",
    textTransform: "capitalize",
    borderBottom: "none",
    borderTop: "none",
    height: HOME_LIST_TABLE_TOKENS.headerHeight,
    px: `${HOME_LIST_TABLE_TOKENS.cellPaddingX}px`,
    py: 0,
  },
} as const;

export const HOME_LIST_TABLE_BODY_ROW_SX = {
  bgcolor: HOME_LIST_TABLE_TOKENS.bodyBackground,
  "& .MuiTableCell-root": {
    bgcolor: HOME_LIST_TABLE_TOKENS.bodyBackground,
    color: HOME_LIST_TABLE_TOKENS.cellColor,
    fontWeight: HOME_LIST_TABLE_TOKENS.cellFontWeight,
    fontSize: HOME_LIST_TABLE_TOKENS.fontSize,
    lineHeight: "20px",
    borderBottom: `1px solid ${HOME_LIST_TABLE_TOKENS.rowDivider}`,
    borderTop: "none",
    minHeight: HOME_LIST_TABLE_TOKENS.rowMinHeight,
    px: `${HOME_LIST_TABLE_TOKENS.cellPaddingX}px`,
    py: "12px",
  },
  "&:last-child .MuiTableCell-root": {
    borderBottom: "none",
  },
} as const;

export const HOME_LIST_TABLE_INACTIVE_TEXT_SX = {
  fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
  fontSize: HOME_LIST_TABLE_TOKENS.fontSize,
  fontWeight: HOME_LIST_TABLE_TOKENS.cellFontWeight,
  lineHeight: "20px",
  color: HOME_LIST_TABLE_TOKENS.inactiveColor,
} as const;

/** Match form-control height so toggling assessment_required does not shift the row. */
export const HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX = {
  display: "flex",
  alignItems: "center",
  minHeight: HOME_LIST_TABLE_TOKENS.cellControlMinHeight,
  width: "100%",
} as const;

/** Compact inputs inside list-style table cells (matches regulations grid filters). */
export const HOME_LIST_TABLE_CELL_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
    fontSize: HOME_LIST_TABLE_TOKENS.fontSize,
    fontWeight: HOME_LIST_TABLE_TOKENS.cellFontWeight,
    color: HOME_LIST_TABLE_TOKENS.cellColor,
    bgcolor: HOME_LIST_TABLE_TOKENS.bodyBackground,
    minHeight: HOME_LIST_TABLE_TOKENS.cellControlMinHeight,
    "& fieldset": {
      borderColor: HOME_LIST_TABLE_TOKENS.inputBorderColor,
    },
    "&:hover fieldset": {
      borderColor: HOME_LIST_TABLE_TOKENS.inputBorderColor,
    },
    "&.Mui-focused fieldset": {
      borderColor: HOME_LIST_TABLE_TOKENS.focusBorderColor,
      borderWidth: "1px",
    },
  },
  "& .MuiSelect-select": {
    fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
    fontSize: HOME_LIST_TABLE_TOKENS.fontSize,
    fontWeight: HOME_LIST_TABLE_TOKENS.cellFontWeight,
    py: "8px",
  },
  "& .MuiInputBase-input": {
    fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
    fontSize: HOME_LIST_TABLE_TOKENS.fontSize,
    fontWeight: HOME_LIST_TABLE_TOKENS.cellFontWeight,
    py: "8px",
  },
} as const;
